import { getDocumentOrThrow } from "@faire/web/common/globals/getDocument";
import { Column, IFlexProps } from "@faire/web/slate/Layout";
import { Placement } from "@popperjs/core";
import React from "react";
import ReactDOM from "react-dom";
import { usePopper } from "react-popper";
import styled from "styled-components";

interface IProps {
  children?: React.ReactNode | undefined;
  tooltip: React.ReactNode;
  placement: Placement;
  tooltipZIndex?: number;
  className?: string;
  xOffset?: number;
  yOffset?: number;
  onMouseOver?: () => void;
  "data-test-id"?: string;
  align?: IFlexProps["align"];
}

/**
 * This is a wrapper wraps web-ui's tooltip in the so it can be positioned
 * with popper.js, and overcome some limitation with position: absolute
 */
export const ReactPortalTooltip: React.FC<IProps> = (props) => {
  const bodyRef = React.useRef(getDocumentOrThrow().body);

  const [wrapperNode, setWrapperNode] = React.useState<HTMLDivElement | null>(
    null
  );
  const [tooltipNode, setTooltipNode] = React.useState<HTMLDivElement | null>(
    null
  );
  const [isOpen, setIsOpen] = React.useState<boolean>(false);

  const { styles, attributes } = usePopper(wrapperNode, tooltipNode, {
    placement: props.placement,
    modifiers: [
      {
        name: "offset",
        enabled: true,
        options: {
          offset: [props.xOffset ?? 0, props.yOffset ?? -20],
        },
      },
    ],
  });

  const handleMouseOver = () => {
    setIsOpen(true);
    props.onMouseOver?.();
  };

  const handleMouseOut = () => {
    setIsOpen(false);
  };

  return (
    <Column
      className={props.className}
      data-test-id={props["data-test-id"]}
      ref={setWrapperNode}
      style={styles.reference}
      {...attributes.reference}
      onMouseEnter={handleMouseOver}
      onMouseLeave={handleMouseOut}
      justify="center"
      align={props.align ?? "center"}
    >
      {props.children}
      {isOpen
        ? ReactDOM.createPortal(
            <Tooltip
              ref={setTooltipNode}
              style={{
                ...styles.popper,
              }}
              {...attributes.popper}
              $zIndex={props.tooltipZIndex}
            >
              {props.tooltip}
            </Tooltip>,
            bodyRef.current
          )
        : null}
    </Column>
  );
};

const Tooltip = styled.div<{ $zIndex?: number }>`
  display: block;
  z-index: ${({ $zIndex }) => $zIndex ?? 5};
`;
